<template>
  <div class="content-alumni-member-list">
    <form class="search-container copy-regular" role="search" @submit.prevent>
      <label for="search" class="title text-2xl">{{ t('search') }}</label>
      <div class="input-wrapper">
        <input
          id="search"
          v-model="search"
          type="search"
          name="search"
          :placeholder="t('searchPlaceholder')"
          :disabled="!canSearch"
        />
        <SvgSearch aria-hidden="true" class="search-icon" />
      </div>
      <output for="search" class="search-results" aria-live="polite">
        {{ resultCount || t('noResults') }} {{ resultCount === 1 ? t('person') : t('people') }} {{ t('found') }}
      </output>
    </form>

    <div v-if="status === 'success'" class="inner">
      <div v-for="member in members" :key="`${member.id}`" class="member-card">
        <img
          :src="member.imageUrl"
          alt="alumni member profile picture"
          class="profile-picture"
          :class="{ '-no-profession-details': !member.occupation && !member.employer }"
        />
        <div
          class="member-details accordion text-md-xs text-md-sm text-sm-md text-sm-lg"
          :class="{
            '-open': isMemberOpen(String(member.id)),
            '-no-profession-details': !member.occupation && !member.employer,
          }"
          @click="toggleMember(String(member.id))"
        >
          <button
            :id="`accordion-${member.id}`"
            class="accordion-header"
            type="button"
            :aria-expanded="isMemberOpen(String(member.id))"
            :aria-controls="'accordion-content-' + member.id"
          >
            <div class="name-wrapper">
              <p class="name text-md-bold-xs text-md-bold-sm text-sm-bold-md text-sm-bold-lg">
                {{ member.nameAffix }}
                {{ member.firstName }}
                {{ member.lastName }}
              </p>
              <SvgMinus v-if="isMemberOpen(String(member.id))" class="icon -minus" aria-hidden="true" />
              <SvgPlus v-else class="icon -plus" aria-hidden="true" />
            </div>
            <div v-if="member.occupation || member.employer" class="profession">
              <p v-if="member.occupation" class="job-title">{{ member.occupation }}</p>
              <p v-if="member.employer" class="company">{{ member.employer }}</p>
            </div>
          </button>
          <div
            :id="'accordion-content-' + member.id"
            class="accordion-content"
            role="region"
            :aria-labelledby="`accordion-${member.id}`"
            :aria-hidden="!isMemberOpen(String(member.id))"
          >
            <div class="content">
              <div v-if="member.graduationYear || member.fieldOfStudy" class="study-details">
                <p v-if="member.graduationYear" class="graduation-year">
                  {{ t('graduation') }} {{ member.graduationYear }}
                </p>
                <p v-if="member.fieldOfStudy" class="field-of-study">{{ member.fieldOfStudy }}</p>
              </div>
              <div v-if="member.email || member.linkedIn" class="contact-details">
                <a
                  v-if="member.email"
                  :href="`mailto:${member.email}`"
                  target="_blank"
                  class="button-link -secondary email"
                  :tabindex="isMemberOpen(String(member.id)) ? 0 : -1"
                >
                  E-Mail
                </a>
                <a
                  v-if="member.linkedIn"
                  :href="member.linkedIn"
                  target="_blank"
                  class="button-link -secondary linkedin"
                  :tabindex="isMemberOpen(String(member.id)) ? 0 : -1"
                >
                  Linkedin
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="status === 'pending' || status === 'idle'" class="inner -skeleton">
      <span class="screenreader-only" role="status">Loading alumni members...</span>
      <div v-for="n in 12" :key="n" class="member-card" aria-hidden="true" />
    </div>

    <nav v-if="resultCount > 0" aria-label="pagination" class="pagination copy-regular">
      <ul class="list">
        <li class="item -back">
          <button type="button" class="button-link -secondary link" :disabled="page === 0" @click="page--">
            <span class="_hidden-mobile">{{ t('back') }}</span>
            <SvgArrowLeft class="_hidden-desktop" aria-label="previous page" />
          </button>
        </li>
        <li v-for="(p, index) in paginationConfig" :key="index" class="item">
          <button
            v-if="p >= 0"
            type="button"
            class="button-link -secondary link"
            :class="{ '-active': p === page }"
            @click="page = p"
          >
            <span class="screenreader-only">page </span>{{ p + 1 }}
          </button>
          <span v-else class="ellipsis">...</span>
        </li>
        <li class="item -forward">
          <button type="button" class="button-link -secondary link" :disabled="page === pageCount - 1" @click="page++">
            <span class="_hidden-mobile">{{ t('forward') }}</span>
            <SvgArrowRight class="_hidden-desktop" aria-label="next page" />
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<i18n lang="yaml">
de:
  search: Suche
  searchPlaceholder: Suchbegriff eingeben
  noResults: Keine
  person: Person
  people: Personen
  found: gefunden
  graduation: Abschluss
  back: Zurück
  forward: Weiter

en:
  search: Search
  searchPlaceholder: Enter search term
  noResults: No
  person: person
  people: people
  found: found
  graduation: Graduation
  back: Back
  forward: Forward
</i18n>

<script setup lang="ts">
import SvgPlus from '../svgs/SvgPlus.vue';
import SvgMinus from '../svgs/SvgMinus.vue';
import SvgSearch from '../svgs/SvgSearch.vue';
import SvgArrowLeft from '~/components/site-header/svgs/SvgArrowLeft.vue';
import SvgArrowRight from '~/components/site-header/svgs/SvgArrowRight.vue';

const { t } = useI18n();

const members = computed(() => data.value?.members ?? []);
const resultCount = computed(() => data.value?.resultCount ?? 0);
const pageCount = computed(() => data.value?.pageCount ?? 0);

const openMembers = ref<Record<string, boolean>>({});

function toggleMember(memberId: string | number) {
  const id = String(memberId);
  openMembers.value[id] = !openMembers.value[id];
}

function isMemberOpen(memberId: string | number) {
  const id = String(memberId);
  return openMembers.value[id] || false;
}

const page = ref(0);
const search = ref<string>();
const searchDebounced = refDebounced(search, 400);

watch(searchDebounced, () => {
  page.value = 0;
});

const paginationConfig = computed(() => {
  if (pageCount.value === 0) {
    return [];
  }

  const config: number[] = [];
  const currentPage = page.value;
  const totalPages = pageCount.value;

  // If we have 7 or fewer pages, show all pages
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) {
      config.push(i);
    }
    return config;
  }

  // Always show exactly 6 elements
  if (currentPage <= 2) {
    // Show: e.g. 1 2 3 4 ... 40
    for (let i = 0; i < 4; i++) {
      config.push(i);
    }
    config.push(-1); // ellipsis
    config.push(totalPages - 1); // last page
  } else if (currentPage >= totalPages - 3) {
    // Show: e.g. 1 ... 37 38 39 40
    config.push(0); // first page
    config.push(-1); // ellipsis
    for (let i = totalPages - 4; i < totalPages; i++) {
      config.push(i);
    }
  } else {
    // Show: e.g. 1 ... 3 4 ... 40
    config.push(0); // first page
    config.push(-1); // ellipsis
    for (let i = currentPage - 1; i <= currentPage; i++) {
      config.push(i);
    }
    config.push(-1); // ellipsis
    config.push(totalPages - 1); // last page
  }

  return config;
});

const { data, status } = useLazyFetch('/api/alumni/members', {
  query: {
    page,
    search: computed(() => searchDebounced.value),
  },
  server: false, // Always load data client side
});

const canSearch = ref(false);
watch(status, () => {
  if (status.value === 'success') {
    // wait until the first load to activate search
    canSearch.value = true;
  }
});
</script>

<style scoped lang="scss">
.content-alumni-member-list {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
  }

  > .search-container {
    display: grid;
    gap: var(--spacing-2xs);

    @media (--xs) {
      grid-column: 1 / -1;
    }

    @media (--sm) {
      grid-column: 2 / 24;
    }

    @media (--md), (--lg) {
      grid-column: 5 / 21;
    }

    > .title {
      color: var(--base-color-brand);
    }

    > .input-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 100px;
      border: 1px solid var(--base-color-foreground);
      padding: var(--spacing-4xs) 0.875rem;
      height: 2.5rem;

      &:focus-within {
        border: 1px solid var(--base-color-brand);
        outline: 1px solid var(--base-color-brand);
      }

      > input#search {
        width: 100%;

        &::-webkit-search-cancel-button {
          -webkit-appearance: none;
          height: 24px;
          width: 24px;
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 4L20 20M4 20L20 4" stroke="%232350ac" stroke-width="2" stroke-miterlimit="10"/></svg>');
          background-repeat: no-repeat;
          background-size: contain;
          cursor: pointer;
          padding-right: 0.25rem;
        }
      }

      > .search-icon {
        cursor: pointer;
      }
    }
  }

  > .inner {
    --height-member-card-large: 22.875rem;
    --height-member-card-small: 25.125rem;
    --height-profile-picture-large: 15.75rem;
    --height-profile-picture-small: 17.75rem;
    --height-profile-picture-no-details-large: 19.25rem;
    --height-profile-picture-no-details-small: 21.5rem;

    display: grid;

    @media (--xs), (--sm) {
      grid-column: 1 / -1;
      padding-top: var(--spacing-2xs);
      row-gap: var(--spacing-2xs);
    }

    @media (--sm) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (--sm), (--md), (--lg) {
      column-gap: var(--spacing-xs);
      row-gap: var(--spacing-2xl);
    }

    @media (--md), (--lg) {
      grid-column: 3 / 23;
      padding: var(--spacing-4xl) 0 var(--spacing-2xl);
    }

    @media (--md), (--lg) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (--lg-custom) {
      grid-template-columns: repeat(4, 1fr);
    }

    &.-skeleton {
      > .member-card {
        &::after {
          @include pseudo();

          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
          transform: translateX(-100%);
          z-index: 1;
          animation: skeleton-loading 1.5s infinite;
        }
      }
    }

    > .member-card {
      background-color: var(--color-main-blue-light);
      position: relative;
      overflow: hidden;

      @media (--xs), (--sm) {
        height: var(--height-member-card-small);
      }

      @media (--md), (--lg) {
        height: var(--height-member-card-large);
      }

      > .profile-picture {
        width: 100%;
        object-fit: cover;
        object-position: top;
        pointer-events: none;
        user-select: none;

        @media (--xs), (--sm) {
          height: var(--height-profile-picture-small);
        }

        @media (--md), (--lg) {
          height: var(--height-profile-picture-large);
        }

        &.-no-profession-details {
          @media (--xs), (--sm) {
            height: var(--height-profile-picture-no-details-small);
          }

          @media (--md), (--lg) {
            height: var(--height-profile-picture-no-details-large);
          }
        }
      }

      > .member-details {
        position: absolute;
        background-color: var(--color-main-blue-light);
        width: 100%;
        height: 100%;
        transition: transform 300ms ease-out;

        cursor: pointer;
        padding-bottom: var(--spacing-3xs);

        &.-open {
          overflow-y: auto;
          scrollbar-color: var(--color-forest-dark) transparent;
          scrollbar-width: thin;

          @media (--xs), (--sm) {
            transform: translateY(calc(var(--height-profile-picture-small) * -1));
          }

          @media (--md), (--lg) {
            transform: translateY(calc(var(--height-profile-picture-large) * -1));
          }

          &.-no-profession-details {
            @media (--xs), (--sm) {
              transform: translateY(calc(var(--height-profile-picture-no-details-small) * -1));
            }

            @media (--md), (--lg) {
              transform: translateY(calc(var(--height-profile-picture-no-details-large) * -1));
            }
          }

          > .accordion-header {
            padding-bottom: 0;
          }

          > .accordion-header > .profession > .job-title,
          > .accordion-header > .profession > .company,
          > .accordion-content > .content > .study-details > .graduation-year,
          > .accordion-content > .content > .study-details > .field-of-study {
            border-bottom: 0.5px solid rgba(35, 80, 172, 0.15); // --color-main-blue: #2350ac, opacity: 0.15
            padding-block: var(--spacing-4xs);
          }
        }

        &:not(.-open) {
          > .accordion-header {
            gap: var(--spacing-4xs);

            > .name-wrapper > .name,
            > .profession > .job-title,
            > .profession > .company {
              display: -webkit-box;
              -webkit-line-clamp: 1;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
          }

          &:not(.-no-profession-details) {
            > .accordion-header {
              min-height: 7.125rem;
            }
          }
        }

        > .accordion-header {
          width: 100%;
          text-align: start;
          background: none;
          padding: var(--spacing-3xs);
          cursor: pointer;
          display: flex;
          flex-direction: column;

          &:focus-visible {
            border-radius: 4px;
            border: 2px solid var(--color-sky);
            transition: none;
          }

          > .name-wrapper,
          > .profession {
            width: 100%;
          }

          > .name-wrapper {
            display: flex;
            justify-content: space-between;
            gap: var(--spacing-3xs);
            pointer-events: auto;

            > .icon {
              &.-minus {
                @media (--md), (--lg) {
                  margin-top: 0.0625rem;
                }
              }

              &.-plus {
                @media (--xs), (--sm) {
                  margin-top: 0.1875rem;
                }

                @media (--md), (--lg) {
                  margin-top: 0.25rem;
                }
              }
            }
          }
        }

        > .accordion-content {
          padding-inline: var(--spacing-3xs);

          > .content {
            > .contact-details {
              display: grid;
              justify-items: start;
              gap: var(--spacing-4xs);

              @media (--xs), (--sm) {
                margin-top: var(--spacing-3xs);
              }

              @media (--md), (--lg) {
                margin-top: var(--spacing-2xs);
              }

              > .button-link {
                @media (--md), (--lg) {
                  font-size: 1rem;
                }
              }
            }
          }
        }
      }
    }
  }

  > .pagination {
    grid-column: 1 / -1;
    justify-self: center;

    @media (--xs), (--sm) {
      padding: var(--spacing-xl) 0 var(--spacing-xs);
    }

    @media (--md), (--lg) {
      padding: var(--spacing-sm) 0 var(--spacing-4xl);
    }

    > .list {
      display: flex;

      @media (--xs), (--sm) {
        gap: var(--spacing-3xs);
        flex-wrap: wrap;
      }

      @media (--md), (--lg) {
        gap: var(--spacing-4xs);
      }

      > .item {
        &.-back {
          @media (--xs) {
            padding-right: var(--spacing-4xs);
          }

          @media (--sm), (--md), (--lg) {
            padding-right: calc(var(--spacing-xs) - var(--spacing-4xs));
          }
        }

        &.-forward {
          @media (--xs) {
            padding-left: var(--spacing-4xs);
          }

          @media (--sm), (--md), (--lg) {
            padding-left: calc(var(--spacing-xs) - var(--spacing-4xs));
          }
        }

        > .link {
          border: none;

          &:disabled {
            pointer-events: auto;
            cursor: not-allowed;
            opacity: 0.5;
          }

          &.-active {
            font-weight: bold;
            pointer-events: none;
          }
        }

        > .ellipsis {
          color: var(--base-color-brand);
        }
      }
    }
  }
}

.screenreader-only {
  @include hide-visually;
}

@keyframes skeleton-loading {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}
</style>
